import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Bell,
    FileText,
    Ship,
    Package,
    AlertCircle,
    CheckCircle2,
    Clock,
    Trash2,
    CheckCheck,
    Filter,
    Search,
    RefreshCw,
    ChevronRight,
    Settings,
    X,
    Mail,
    ClipboardCheck,
    PlusCircle,
    AlertTriangle,
    CheckSquare
} from 'lucide-react';
import { baseURL } from '@/config/APIConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = baseURL.live;
const dashboardApi = baseURL.dashboardApi;

// Notification type configurations with icons and colors
const NOTIFICATION_CONFIG = {
    // Document AI Notifications
    document_uploaded: { icon: FileText, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    document_ai_completed: { icon: CheckCircle2, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    document_validation_warning: { icon: AlertTriangle, color: 'amber', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
    document_review_needed: { icon: AlertCircle, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30' },

    // Container Notifications
    container_event: { icon: Package, color: 'purple', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
    container_location: { icon: Ship, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    container_status_alert: { icon: AlertCircle, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30' },

    // Booking Notifications
    booking_created: { icon: PlusCircle, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    booking_status_changed: { icon: RefreshCw, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    booking_confirmed: { icon: CheckCircle2, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    shipment_missing_docs: { icon: AlertCircle, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30' },

    // Status Audit Notifications
    status_audit_changed: { icon: ClipboardCheck, color: 'purple', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
    audit_action_required: { icon: Clock, color: 'amber', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
    audit_completed: { icon: CheckSquare, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30' },

    // Quote Notifications
    quote_request_received: { icon: FileText, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    vendor_response: { icon: CheckCircle2, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    quote_expiring: { icon: Clock, color: 'amber', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },

    // Email Notifications
    email_received: { icon: Mail, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    ai_extraction_completed: { icon: CheckCircle2, color: 'green', bgColor: 'bg-green-100 dark:bg-green-900/30' },

    // System Notifications
    user_login: { icon: CheckCircle2, color: 'slate', bgColor: 'bg-slate-100 dark:bg-slate-700/30' },
    system_maintenance: { icon: Settings, color: 'amber', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
    error_alert: { icon: AlertCircle, color: 'red', bgColor: 'bg-red-100 dark:bg-red-900/30' },

    // Default
    default: { icon: Bell, color: 'blue', bgColor: 'bg-blue-100 dark:bg-blue-900/30' }
};

const COLOR_CLASSES = {
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-green-600 dark:text-green-400',
    red: 'text-red-600 dark:text-red-400',
    amber: 'text-amber-600 dark:text-amber-400',
    purple: 'text-purple-600 dark:text-purple-400',
    slate: 'text-slate-600 dark:text-slate-400',
};

const CATEGORIES = [
    { id: 'all', label: 'All', icon: Bell },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'containers', label: 'Containers', icon: Package },
    { id: 'bookings', label: 'Bookings', icon: Ship },
    { id: 'system', label: 'System', icon: Settings },
];

const NotificationCenterPage = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryCounts, setCategoryCounts] = useState({});
    const [unreadCount, setUnreadCount] = useState(0);
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);

    const fetchNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (activeCategory !== 'all') {
                params.append('category', activeCategory);
            }
            if (showUnreadOnly) {
                params.append('unread_only', 'true');
            }
            params.append('limit', '100');

            const response = await axios.get(`${dashboardApi}/notifications?${params.toString()}`);
            setNotifications(response.data.notifications || []);
            setCategoryCounts(response.data.category_counts || {});
            setUnreadCount(response.data.unread_count || 0);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [activeCategory, showUnreadOnly]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const markAsRead = async (notificationId) => {
        try {
            await axios.patch(`${dashboardApi}/notifications/${notificationId}/read`);
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await axios.patch(`${dashboardApi}/notifications/read-all`);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const deleteNotification = async (notificationId) => {
        try {
            await axios.delete(`${dashboardApi}/notifications/${notificationId}`);
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    const clearAllNotifications = async () => {
        try {
            await axios.delete(`${dashboardApi}/notifications`);
            setNotifications([]);
            setUnreadCount(0);
            setCategoryCounts({ all: 0, documents: 0, containers: 0, bookings: 0, system: 0 });
        } catch (error) {
            console.error('Error clearing notifications:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
        }
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
        return date.toLocaleDateString();
    };

    const getNotificationConfig = (type) => {
        return NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.default;
    };

    // Filter notifications by search query
    const filteredNotifications = notifications.filter(n => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            n.title?.toLowerCase().includes(query) ||
            n.message?.toLowerCase().includes(query)
        );
    });

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 w-full">
            {/* Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
                <div className="w-full px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 gap-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <Bell className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                                    Notification Center
                                </h1>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={fetchNotifications}
                                className="gap-2"
                            >
                                <RefreshCw className="w-4 h-4" />
                                <span className="hidden sm:inline">Refresh</span>
                            </Button>
                            {unreadCount > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={markAllAsRead}
                                    className="gap-2"
                                >
                                    <CheckCheck className="w-4 h-4" />
                                    <span className="hidden sm:inline">Mark all read</span>
                                </Button>
                            )}
                            {notifications.length > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={clearAllNotifications}
                                    className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    <span className="hidden sm:inline">Clear all</span>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
                {/* Filters Row */}
                <div className="flex flex-col lg:flex-row gap-4 mb-6">
                    {/* Category Tabs */}
                    <div className="flex flex-wrap gap-2">
                        {CATEGORIES.map((category) => {
                            const Icon = category.icon;
                            const count = category.id === 'all'
                                ? Object.values(categoryCounts).reduce((a, b) => a + b, 0)
                                : categoryCounts[category.id] || 0;

                            return (
                                <button
                                    key={category.id}
                                    onClick={() => setActiveCategory(category.id)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${activeCategory === category.id
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span>{category.label}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${activeCategory === category.id
                                        ? 'bg-white/20 text-white'
                                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                        }`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Search and Filters */}
                    <div className="flex flex-1 gap-2 lg:justify-end">
                        <div className="relative flex-1 lg:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                type="text"
                                placeholder="Search notifications..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 bg-white dark:bg-slate-800"
                            />
                        </div>
                        <Button
                            variant={showUnreadOnly ? "default" : "outline"}
                            size="sm"
                            onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                            className="gap-2 whitespace-nowrap"
                        >
                            <Filter className="w-4 h-4" />
                            <span className="hidden sm:inline">Unread only</span>
                        </Button>
                    </div>
                </div>

                {/* Notifications List */}
                <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : filteredNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-500 dark:text-slate-400">
                            <Bell className="w-12 h-12 mb-4 opacity-50" />
                            <p className="text-lg font-medium">No notifications</p>
                            <p className="text-sm">
                                {searchQuery ? 'No notifications match your search' : 'You\'re all caught up!'}
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100 dark:divide-slate-700">
                            <AnimatePresence>
                                {filteredNotifications.map((notification, index) => {
                                    const config = getNotificationConfig(notification.type);
                                    const Icon = config.icon;
                                    const colorClass = COLOR_CLASSES[config.color] || COLOR_CLASSES.blue;

                                    return (
                                        <motion.div
                                            key={notification.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, x: -100 }}
                                            transition={{ delay: index * 0.03 }}
                                            className={`relative group ${!notification.read
                                                ? 'bg-blue-50/50 dark:bg-blue-900/10'
                                                : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                                                }`}
                                        >
                                            <div
                                                className="flex items-start gap-4 p-4 sm:p-5 cursor-pointer"
                                                onClick={() => handleNotificationClick(notification)}
                                            >
                                                {/* Icon */}
                                                <div className={`flex-shrink-0 p-2.5 rounded-xl ${config.bgColor}`}>
                                                    <Icon className={`w-5 h-5 ${colorClass}`} />
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className={`text-sm font-semibold ${!notification.read
                                                                ? 'text-slate-900 dark:text-white'
                                                                : 'text-slate-700 dark:text-slate-300'
                                                                }`}>
                                                                {notification.title}
                                                            </h3>
                                                            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                                                                {notification.message}
                                                            </p>

                                                            {/* Metadata */}
                                                            {notification.metadata && Object.keys(notification.metadata).length > 0 && (
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    {notification.metadata.changes && (
                                                                        <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">
                                                                            {notification.metadata.changes.length} status change(s)
                                                                        </div>
                                                                    )}
                                                                    {notification.metadata.file_number && (
                                                                        <div className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">
                                                                            File: {notification.metadata.file_number}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Time and Actions */}
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                                                {getTimeAgo(notification.created_at)}
                                                            </span>

                                                            {/* Unread indicator */}
                                                            {!notification.read && (
                                                                <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Link indicator */}
                                                    {notification.link && (
                                                        <div className="flex items-center gap-1 mt-2 text-xs text-blue-600 dark:text-blue-400">
                                                            <span>View details</span>
                                                            <ChevronRight className="w-3 h-3" />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Actions (visible on hover) */}
                                                <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {!notification.read && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                markAsRead(notification.id);
                                                            }}
                                                            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                                            title="Mark as read"
                                                        >
                                                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            deleteNotification(notification.id);
                                                        }}
                                                        className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-500" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Priority indicator */}
                                            {notification.priority === 'high' && (
                                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />
                                            )}
                                            {notification.priority === 'critical' && (
                                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500" />
                                            )}
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </div>
                    )}
                </div>

                {/* Stats Footer */}
                {!loading && filteredNotifications.length > 0 && (
                    <div className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
                        Showing {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
                        {searchQuery && ` matching "${searchQuery}"`}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationCenterPage;
